import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import PromoBanner from "@/components/PromoBanner";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleGoogle = (redirectPath = "/dashboard") => {
  const redirectUrl = window.location.origin + redirectPath;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// FastAPI 422 returns detail as an array of {msg,...}. Format safely so React never crashes.
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ") || "Please check your inputs.";
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const inputCls =
  "w-full bg-white border border-[#E4DFD2] rounded-lg px-3 py-2.5 text-sm text-[#1F2326] placeholder-[#A9A294] focus:outline-none focus:border-[#1F2326] focus:ring-1 focus:ring-[#1F2326] transition-colors";

const GoogleButton = ({ children = "Continue with Google", testid = "auth-google-btn" }) => (
  <button
    type="button"
    onClick={() => handleGoogle()}
    data-testid={testid}
    className="w-full inline-flex items-center justify-center gap-2 bg-white border border-[#E4DFD2] hover:border-[#B89B5E] text-[#1F2326] px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
  >
    <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.67-2.26 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95l3.66-2.84Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z" />
    </svg>
    {children}
  </button>
);

const PasswordField = ({ value, onChange, placeholder, testid, autoComplete = "new-password" }) => {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input
        type={shown ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        minLength={8}
        required
        data-testid={testid}
        className={`${inputCls} pr-10`}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        tabIndex={-1}
        aria-label={shown ? "Hide password" : "Show password"}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#A9A294] hover:text-[#1F2326] p-1"
      >
        {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const AuthPage = ({ mode = "signup" }) => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const titles = {
      signup: "Sign up · Retainer Tracker",
      login: "Log in · Retainer Tracker",
      forgot: "Reset password · Retainer Tracker",
      reset: "Set a new password · Retainer Tracker",
    };
    document.title = titles[mode] || "Retainer Tracker";
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "signup") {
        const r = await api.post("/auth/signup", { email, password, name });
        setUser(r.data.user);
        toast.success("Welcome — your dashboard is ready.");
        navigate("/dashboard", { replace: true });
      } else if (mode === "login") {
        const r = await api.post("/auth/login", { email, password });
        setUser(r.data.user);
        navigate("/dashboard", { replace: true });
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setSubmitted(true);
      } else if (mode === "reset") {
        const token = params.get("token");
        if (!token) {
          toast.error("Reset link is missing the token.");
          return;
        }
        const r = await api.post("/auth/reset-password", { token, password });
        setUser(r.data.user);
        toast.success("Password updated. You're signed in.");
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Could not complete this. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    signup: "Create your Retainer Tracker account",
    login: "Log in to Retainer Tracker",
    forgot: "Reset your password",
    reset: "Set a new password",
  };
  const subtitles = {
    signup: "14-day free trial. No credit card needed.",
    login: "Welcome back.",
    forgot: "Enter the email you used to sign up. We'll send a reset link.",
    reset: "Choose a new password to get back in.",
  };

  return (
    <div className="min-h-screen bg-[#F8F6F1] text-[#1F2326] flex flex-col" data-testid={`auth-${mode}-page`}>
      <PromoBanner />
      <header className="border-b border-[#E4DFD2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><Logo size={28} showTagline /></Link>
          <Link to="/pricing" className="text-sm text-[#69747C] hover:text-[#1F2326]">Pricing</Link>
        </div>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-md bg-white border border-[#E4DFD2] rounded-2xl p-8">
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#1F2326]">
            {titles[mode]}
          </h1>
          <p className="text-sm text-[#69747C] mt-2">{subtitles[mode]}</p>

          {(mode === "signup" || mode === "login") && (
            <div className="mt-6 space-y-3">
              <GoogleButton testid={`auth-${mode}-google`} />
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-[#A9A294]">
                <div className="flex-1 h-px bg-[#E4DFD2]" />
                Or
                <div className="flex-1 h-px bg-[#E4DFD2]" />
              </div>
            </div>
          )}

          {mode === "forgot" && submitted ? (
            <div
              data-testid="forgot-submitted"
              className="mt-6 bg-[#EFF3EE] border border-[#C5D5C8] text-[#42624B] rounded-lg px-4 py-3 text-sm"
            >
              If an account exists for <span className="font-medium">{email}</span>, we've sent a password reset link. Check your inbox.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-4 space-y-3">
              {mode === "signup" && (
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  autoComplete="name"
                  data-testid="auth-name-input"
                  className={inputCls}
                />
              )}
              {(mode === "signup" || mode === "login" || mode === "forgot") && (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@studio.com"
                  autoComplete="email"
                  required
                  data-testid="auth-email-input"
                  className={inputCls}
                />
              )}
              {(mode === "signup" || mode === "login" || mode === "reset") && (
                <PasswordField
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "login" ? "Your password" : "Choose a password (8+ characters)"}
                  testid="auth-password-input"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                />
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid={`auth-${mode}-submit`}
                className="w-full bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] disabled:opacity-60 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
              >
                {busy
                  ? "Working..."
                  : mode === "signup"
                  ? "Create account"
                  : mode === "login"
                  ? "Log in"
                  : mode === "forgot"
                  ? "Send reset link"
                  : "Set new password"}
              </button>
            </form>
          )}

          <div className="mt-5 text-xs text-[#69747C] flex flex-wrap gap-x-4 gap-y-1">
            {mode === "signup" && (
              <>
                <Link to="/login" className="hover:text-[#1F2326]" data-testid="auth-link-login">
                  Have an account? Log in
                </Link>
              </>
            )}
            {mode === "login" && (
              <>
                <Link to="/signup" className="hover:text-[#1F2326]" data-testid="auth-link-signup">
                  Create an account
                </Link>
                <Link to="/forgot-password" className="hover:text-[#1F2326]" data-testid="auth-link-forgot">
                  Forgot password?
                </Link>
              </>
            )}
            {(mode === "forgot" || mode === "reset") && (
              <Link to="/login" className="hover:text-[#1F2326]">Back to login</Link>
            )}
          </div>

          {mode === "signup" && (
            <p className="mt-6 text-[11px] text-[#A9A294] leading-relaxed">
              By creating an account you agree to our{" "}
              <Link to="/terms" className="underline hover:text-[#1F2326]">Terms</Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline hover:text-[#1F2326]">Privacy Policy</Link>.
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AuthPage;
